import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import { useState } from "react";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeValuesText?: TypographyPresentationProps;
    tabDocumentContainer?: TabPresentationProps;
    hiddenTabContainer?: TabPresentationProps;
    docContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    tabStyles?: InjectableCss;
    tabNameContent?: TypographyPresentationProps;
    tabContent?: TypographyPresentationProps;
    documentNameText?: TypographyPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
    docContainer: {
        css: css`
            & ${GridOffset} {
                display: grid;
            }
        `,
    },
    hiddenTabContainer: {
        css: css`
            display: none;
        `,
    },
    tabDocumentContainer: {
        css: css`
            cursor: pointer;
            padding: 10px 20px 20px;
        `,
    },
    tabStyles: {
        css: css`
            cursor: pointer;
            padding: 10px 20px 20px;
        `,
    },
    tabNameContent: {
        css: css`
            cursor: pointer;
            font-weight: bold;
        `,
    },
    tabContent: {
        css: css`
            .crossover {
                display: inline-block;
                width: 50%;
            }
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
            grid-auto-flow: column;
        `,
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsAttributeTabs: React.FC<Props> = ({
    // include canshowprice to see if user is logged in, to show documents
    product: { brand, attributeTypes, documents, specifications, canShowPrice },
}) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    interface TabItems {
        [key: string]: boolean;
    }

    const [state, setState] = React.useState({} as TabItems);

    const renderingSpecifications = specifications && specifications.length > 0;
    const tabs: TabItems = {};
    let initialTab = "";
    if (renderingSpecifications) {
        specifications?.forEach(spec => {
            if (initialTab.length === 0) {
                initialTab = spec.name;
            }
            tabs[spec.name] = false;
        });
    }
    if (initialTab.length === 0 && documents) {
        initialTab = "Documents";
    }

    const [currentTab, setMyCurrentTab] = useState(initialTab);

    tabs["Documents"] = false;

    const setCurrentTab = (tabName: string) => {
        Object.keys(tabs).forEach((tabItem: string) => {
            if (tabItem === tabName) {
                setState({ [tabName]: !state[tabName] });
            } else if (state[tabName] === true) {
                setState({ [tabName]: false });
            }
        });
        setMyCurrentTab(tabName);
    };

    const onTabChange = (event: React.MouseEvent | React.KeyboardEvent, tabKey?: string) => {
        if (tabKey) {
            setCurrentTab(tabKey);
        }
    };

    const onClickAccordionSection = (tabKey: string) => () => {
        if (tabKey) {
            const keys = Object.keys(state);
            let previousTab = "";
            keys.forEach(key => {
                if (state[key] === true) {
                    previousTab = key;
                }
            });
            const previousElement =
                state[previousTab] === true ? document?.querySelector(`[data-test-key="${previousTab}"]`) : undefined;
            const previousContentSelector = previousElement
                ? previousElement?.children[0]?.getAttribute("aria-Controls")
                : undefined;
            const previousContent =
                previousContentSelector && previousContentSelector.length > 0
                    ? document?.getElementById(previousContentSelector)
                    : undefined;

            const expanded = state[tabKey];
            setCurrentTab(tabKey);
            if (expanded !== true) {
                const elem = document?.querySelector(`[data-test-key="${tabKey}"]`);
                if (elem) {
                    const elemBounds = elem.getBoundingClientRect();
                    let offset = 0;
                    const previousBounds = previousElement?.getBoundingClientRect();

                    if (previousElement) {
                        if (previousBounds && previousBounds.top < elemBounds.top) {
                            if (previousContent) {
                                const previousContentBounds = previousContent.getBoundingClientRect();
                                const visibleContentHeight = previousContentBounds.height;
                                offset = visibleContentHeight;
                            }
                        }
                    }
                    const y = elemBounds.top + window.pageYOffset - offset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }
            }
        }
    };

    const renderTabs = () => {
        const tabs = [];

        if (specifications) {
            specifications?.forEach(specification => {
                if (specification) {
                    let htmlContent = specification.htmlContent;
                    if (htmlContent.includes("<=")) {
                        htmlContent = htmlContent.replace(/<=/g, "&lt=");
                    }
                    if (htmlContent.includes("=<")) {
                        htmlContent = htmlContent.replace(/=</g, "=&lt");
                    }
                    if (htmlContent.includes(">=")) {
                        htmlContent = htmlContent.replace(/>=/g, "&gt=");
                    }
                    if (htmlContent.includes("=>")) {
                        htmlContent = htmlContent.replace(/=>/g, "=&gt");
                    }
                    tabs.push(
                        <Tab
                            style={specification === undefined ? { display: "none" } : {}}
                            headline={
                                <Typography {...styles.tabNameContent}>{translate(specification.name)}</Typography>
                            }
                            key={specification.name}
                            tabKey={specification.name}
                            hidden={false}
                            {...styles.tabStyles}
                        >
                            <Typography {...styles.tabContent}>{parse(htmlContent, parserOptions)}</Typography>
                        </Tab>,
                    );
                }
            });
        }
        if (documents && documents.length && documents.length > 0 && canShowPrice) {
            tabs.push(
                <Tab
                    {...styles.tabDocumentContainer}
                    style={documents === undefined || documents?.length === 0 ? { display: "none" } : {}}
                    headline={<Typography {...styles.tabNameContent}>{translate("Documents")}</Typography>}
                    key="Documents"
                    tabKey="Documents"
                >
                    <StyledWrapper {...styles.wrapper}>
                        <GridContainer {...styles.docContainer}>
                            {documents?.map(document => (
                                <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                                    <Link
                                        href={document.filePath}
                                        target="_new"
                                        {...styles.documentLink}
                                        data-test-selector={`productDetails_${document.id}`}
                                    >
                                        <IconMemo {...styles.documentIcon} />
                                        <Typography {...styles.documentNameText}>{document.name}</Typography>
                                    </Link>
                                </GridItem>
                            ))}
                        </GridContainer>
                    </StyledWrapper>
                </Tab>,
            );
        }
        tabs.push(
            <Tab {...styles.hiddenTabContainer} headline={translate("None")} key="None" tabKey="None" hidden={true}>
                <></>
            </Tab>,
        );

        return (
            <TabGroup current={currentTab} onTabChange={onTabChange}>
                {tabs}
            </TabGroup>
        );
    };

    const renderAccordion = () => {
        const tabs = [];

        if (specifications) {
            specifications?.forEach(specification => {
                if (specification) {
                    let htmlContent = specification.htmlContent;
                    if (htmlContent.includes("<=")) {
                        htmlContent = htmlContent.replace(/<=/g, "&lt=");
                    }
                    if (htmlContent.includes("=<")) {
                        htmlContent = htmlContent.replace(/=</g, "=&lt");
                    }
                    if (htmlContent.includes(">=")) {
                        htmlContent = htmlContent.replace(/>=/g, "&gt=");
                    }
                    if (htmlContent.includes("=>")) {
                        htmlContent = htmlContent.replace(/=>/g, "=&gt");
                    }
                    tabs.push(
                        <AccordionSection
                            key={specification.name}
                            // {...styles.brandAccordionSection}
                            title={specification.name}
                            expanded={state[specification.name]}
                            onTogglePanel={onClickAccordionSection(specification.name)}
                        >
                            <Typography {...styles.tabContent}>{parse(htmlContent, parserOptions)}</Typography>
                        </AccordionSection>,
                    );
                }
            });
        }
        if (documents && documents.length && documents.length > 0 && canShowPrice) {
            tabs.push(
                <AccordionSection
                    key="Documents"
                    // {...styles.brandAccordionSection}
                    title="Documents"
                    expanded={state["Documents"]}
                    onTogglePanel={onClickAccordionSection("Documents")}
                >
                    <StyledWrapper {...styles.wrapper}>
                        <GridContainer {...styles.docContainer}>
                            {documents?.map(document => (
                                <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                                    <Link
                                        href={document.filePath}
                                        target="_new"
                                        {...styles.documentLink}
                                        data-test-selector={`productDetails_${document.id}`}
                                    >
                                        <IconMemo {...styles.documentIcon} />
                                        <Typography {...styles.documentNameText}>{document.name}</Typography>
                                    </Link>
                                </GridItem>
                            ))}
                        </GridContainer>
                    </StyledWrapper>
                </AccordionSection>,
            );
        }

        return <Accordion headingLevel={2}>{tabs}</Accordion>;
    };

    return (
        <>
            <Hidden below="md">{renderTabs()}</Hidden>
            <Hidden above="sm">{renderAccordion()}</Hidden>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributeTabs),
    definition: {
        displayName: "Product Information Tabs",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
